import React, { useState, useEffect, useRef } from 'react'
import {
  CancelConfirmDialog,
  ProgressDialog,
} from 'apps/studio/components/dialogs'
import { useSelector, useDispatch } from 'react-redux'
import {
  aiProgressSelector,
  aiActiveSelector,
  fileNameSelector,
  plgDemoSelector,
} from 'apps/studio/store/selectors/studioSettings'
import {
  pdfFileSelector,
  pagesSelector,
  layoutsSelector,
} from 'apps/studio/store/selectors/pdf'
import { PDFDocument } from 'pdf-lib'
import { processAiRequest } from 'apps/studio/lib/aiRequests'
import { processAiExtractedFields } from 'apps/studio/lib/utils/aiPdfUtils'
import { AI_MODELS, AI_ACTIONS } from 'lib/ai/aiConstants'
import { allEntryTypesSelector } from 'apps/formsCreationKit/store/selectors/types'
import { loadFormData } from 'apps/formsCreationKit/store/actions/sharedActions'
import { appSettingsActions } from 'apps/formsCreationKit/store/reducers/settings/appSettings'
import { studioSettingsActions } from 'apps/studio/store/reducers/settings/studioSettings'
import { readBlobUrlAsDataURL } from 'apps/studio/lib/utils/pdfData'
const { setAILoadingState, resetAILoadingState } = studioSettingsActions
const { updateFlashMessage } = appSettingsActions
import { appSelector } from 'apps/formsCreationKit/store/selectors/entities'
import { v4 } from 'uuid'

const PROCESSING_UPLOAD_TEXT = 'Processing upload'

const CANCEL_CONFIRM_TITLE = 'Cancel Processing?'
const CANCEL_CONFIRM_TEXT =
  'Are you sure? No fields will be added. You can manually drag fields onto your form.'

const READ_ERROR_MESSAGE =
  'Failed to read PDF file. Please ensure the file is valid and try again.'
const AI_ERROR_MESSAGE =
  'Error communicating with AI service. Please try again later. You can still build your form manually.'

const ESTIMATED_SECONDS_PER_PAGE = 10

const progressMessages = [
  'Scanning your PDF to map out the form...',
  'Translating fields into smart, mobile inputs...',
  'Almost there—putting the final touches on your form...',
]

export const getDocumentBuffer = async blobUrl => {
  const response = await fetch(blobUrl)
  const blob = await response.blob()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsArrayBuffer(blob)
  })
}

const generateAppName = (processedPagesTitles, fileName, currentAppName) => {
  const foundTitle = processedPagesTitles.find(t => t && t.trim().length > 0)
  return (
    foundTitle || (fileName ? fileName.replace(/\.pdf$/i, '') : currentAppName)
  )
}

// Helper function to estimate time remaining
const estimateTimeRemaining = (
  completedPages,
  totalPages,
  secondsPerPage = ESTIMATED_SECONDS_PER_PAGE
) => {
  if (totalPages) {
    const remainingPages = totalPages - completedPages
    if (remainingPages <= 0.01) {
      return 'Almost done!'
    }
    const remainingSeconds = remainingPages * secondsPerPage
    if (remainingSeconds >= 60) {
      const minutes = Math.ceil(remainingSeconds / 60)
      return `About ${minutes} ${
        minutes === 1 ? 'minute' : 'minutes'
      } remaining...`
    } else {
      const seconds = Math.ceil(remainingSeconds)
      return `${seconds} ${seconds === 1 ? 'second' : 'seconds'} remaining...`
    }
  }
  return 'Calculating time...'
}

export const AIProgress = ({ onClose }) => {
  const { completedPages, totalPages } = useSelector(aiProgressSelector)
  const [displayedPercentage, setDisplayedPercentage] = useState(0)
  const animationRef = useRef()
  const prevCompletedPagesRef = useRef(completedPages)
  const pagesToProcess = Number(totalPages)
  const processedPages = Number(completedPages)

  useEffect(() => {
    if (!totalPages) {
      // There is a small delay between when AI loading starts and when totalPages is set.
      // During this time, we don't want to animate anything.
      setDisplayedPercentage(0)
      return
    }

    let start = null

    const lowerBound = (processedPages / pagesToProcess) * 100
    const upperBound = ((processedPages + 1) / pagesToProcess) * 100

    function animate(now) {
      if (!start) start = now

      const duration = ESTIMATED_SECONDS_PER_PAGE * 1000 // 10 seconds per page
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const value = lowerBound + (upperBound - lowerBound) * progress

      setDisplayedPercentage(value)

      if (progress < 1 && processedPages === prevCompletedPagesRef.current) {
        animationRef.current = requestAnimationFrame(animate)
      } else {
        setDisplayedPercentage(upperBound)
      }
    }

    prevCompletedPagesRef.current = processedPages
    setDisplayedPercentage(lowerBound)
    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [completedPages, totalPages])

  // Calculate the "actual" number of completed pages based on the animated progress bar percentage.
  const actualProcessedPages = (displayedPercentage / 100) * pagesToProcess
  const displayedTimeRemaining = estimateTimeRemaining(
    actualProcessedPages,
    pagesToProcess
  )

  return (
    <ProgressDialog
      percentage={displayedPercentage}
      timeRemaining={displayedTimeRemaining}
      statusMessages={progressMessages}
      title={PROCESSING_UPLOAD_TEXT}
      onClose={onClose}
    />
  )
}

export const AIFormLoader = () => {
  const dispatch = useDispatch()
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const entryTypes = useSelector(allEntryTypesSelector)
  const isAIactive = useSelector(aiActiveSelector)
  const blobUrl = useSelector(pdfFileSelector)
  const fileName = useSelector(fileNameSelector)
  const currentApp = useSelector(appSelector)
  const pages = useSelector(pagesSelector)
  const layouts = useSelector(layoutsSelector)
  const plgDemo = useSelector(plgDemoSelector)
  const aiBasePath = plgDemo ? '/product/builder-demo/ai' : '/ai'
  const isCanceledRef = useRef(false)
  const accumulatedFieldsRef = useRef([])
  const eventSourceRef = useRef(null)

  useEffect(() => {
    if (!isAIactive) return

    const controller = new AbortController()
    const { signal } = controller
    isCanceledRef.current = false

    const runAI = async () => {
      let pdfData, totalPages
      try {
        const base64Data = await readBlobUrlAsDataURL(blobUrl)
        const documentBuffer = await getDocumentBuffer(blobUrl)
        const pdfDoc = await PDFDocument.load(documentBuffer)
        totalPages = pdfDoc.getPageCount()
        dispatch(setAILoadingState({ totalPages }))
        if (isCanceledRef.current) return
        pdfData = { mimeType: 'application/pdf', bytes: base64Data }
      } catch {
        dispatch(
          updateFlashMessage({ message: READ_ERROR_MESSAGE, type: 'error' })
        )
        return
      }

      const jobId = v4()
      let processedPageNumber = 0
      let processedPagesTitles = []
      accumulatedFieldsRef.current = []

      const onFields = ({ title, extractedFields }) => {
        accumulatedFieldsRef.current = [
          ...accumulatedFieldsRef.current,
          ...extractedFields,
        ]
        processedPageNumber += 1
        processedPagesTitles.push(title)
        dispatch(
          setAILoadingState({ completedPages: processedPageNumber, totalPages })
        )
      }

      const onComplete = () => {
        const newName = generateAppName(
          processedPagesTitles,
          fileName,
          currentApp.name
        )
        const processedData = processAiExtractedFields(
          accumulatedFieldsRef.current,
          blobUrl,
          entryTypes,
          pages,
          layouts
        )
        dispatch(
          loadFormData({
            ...processedData,
            app: { ...currentApp, name: newName },
          })
        )
        dispatch(
          updateFlashMessage({
            message: 'Form successfully created.',
            type: 'success',
          })
        )
        window.pendo?.track('AI Form Complete', {
          plgDemo,
          pageCount: totalPages,
          fieldCount: accumulatedFieldsRef.current.length,
        })
      }

      const onError = () => {
        dispatch(resetAILoadingState())
        dispatch(
          updateFlashMessage({ message: AI_ERROR_MESSAGE, type: 'error' })
        )
      }

      eventSourceRef.current = createEventSource({
        jobId,
        totalPages,
        blobUrl,
        entryTypes,
        fileName,
        currentApp,
        dispatch,
        onFields,
        onComplete,
        onError,
        basePath: aiBasePath,
      })

      await processAiRequest(
        AI_MODELS.CLAUDE_HAIKU_4_5,
        AI_ACTIONS.ANALYZE_PDF,
        pdfData,
        signal,
        jobId,
        aiBasePath
      )
    }

    runAI().catch(error => {
      if (isCanceledRef.current) return
      if (error?.name !== 'AbortError' && !signal?.aborted) {
        dispatch(
          updateFlashMessage({ message: AI_ERROR_MESSAGE, type: 'error' })
        )
      }
    })

    return () => {
      controller.abort('canceled by user')
      isCanceledRef.current = true
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [isAIactive && pages.length && layouts.length]) // We need to wait until pages and layouts are loaded into the store before we can process the AI extracted fields

  const closeHandler = () => {
    isCanceledRef.current = true
    setShowCancelConfirm(false)
    dispatch(resetAILoadingState())

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }

  return (
    <>
      <AIProgress onClose={() => setShowCancelConfirm(true)} />
      {showCancelConfirm && (
        <CancelConfirmDialog
          onCancel={() => setShowCancelConfirm(false)}
          onConfirm={closeHandler}
          title={CANCEL_CONFIRM_TITLE}
          description={CANCEL_CONFIRM_TEXT}
        />
      )}
    </>
  )
}

const createEventSource = ({
  jobId,
  onFields,
  onComplete,
  onError,
  basePath = '/ai',
}) => {
  const eventSource = new EventSource(
    `${basePath}/stream/${jobId}?ai_action=${AI_ACTIONS.ANALYZE_PDF}`
  )

  const handlePdfStream = event => {
    try {
      const { title, extractedFields = [] } = JSON.parse(event.data)
      onFields({ title, extractedFields })
    } catch (error) {
      console.error('Error processing AI response:', error)
    }
  }

  const handleComplete = () => {
    eventSource.close()
    onComplete()
  }

  const handleError = error => {
    console.error('Error in AI processing:', error)
    eventSource.close()
    onError()
  }

  eventSource.addEventListener('PdfStream', handlePdfStream)
  eventSource.addEventListener('complete', handleComplete)
  eventSource.onerror = handleError

  return eventSource
}
